import React, { useState } from 'react';
import { CalendarToday, Receipt } from '@mui/icons-material';
import { WorkItem, Appointment, JobWithNotes } from '../workItemTypes';
import { WorkNotesSection } from './WorkNotesSection';
import { AppointmentsSection } from './AppointmentsSection';
import { AppointmentForm } from './AppointmentForm';
import { WorkNotesHandlers } from '../hooks/useWorkNotes';
import { AppointmentsHandlers } from '../hooks/useAppointments';
import { formatTimestamp } from '../utils/workItemUtils';

export interface WorkItemDetailsProps {
  workItem: WorkItem;
  workItems: WorkItem[];
  jobs: JobWithNotes[];
  appointments: Appointment[];
  workNotesHandlers: WorkNotesHandlers;
  appointmentsHandlers: AppointmentsHandlers;
  onAddInvoiceItem?: () => void;
}

export const WorkItemDetails: React.FC<WorkItemDetailsProps> = ({
  workItem,
  // workItems, // Currently unused but may be needed for future features
  jobs,
  appointments,
  workNotesHandlers,
  appointmentsHandlers,
  onAddInvoiceItem
}) => {
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const { editingAppointmentId, setEditingAppointmentId } = appointmentsHandlers;

  // Get the corresponding job or appointment data
  const job = workItem.type === 'job' ? jobs.find(j => j.id === workItem.id) : null;
  const appointment = workItem.type === 'appointment' ? appointments.find(a => a.id === workItem.id) : null;

  const handleAddAppointment = () => {
    // Clear any existing edit state
    setEditingAppointmentId(null);
    setShowAppointmentForm(true);
  };

  const handleCloseAppointmentForm = () => {
    setShowAppointmentForm(false);
    // Clear edit state when closing
    setEditingAppointmentId(null);
  };

  // Show form when editing an appointment
  React.useEffect(() => {
    if (editingAppointmentId) {
      setShowAppointmentForm(true);
    }
  }, [editingAppointmentId]);

  return (
    <div className="workitem-details-expanded">
      <div className="workitem-details-content">
        <div className="workitem-details-section">
          <div className="section-header">
            <h4>Work Item Details</h4>
            <div className="section-header-actions">
              {workItem.type === 'job' && (
                <button
                  type="button"
                  className="icon-btn"
                  onClick={handleAddAppointment}
                  title="Add Appointment"
                >
                  <CalendarToday style={{ fontSize: '16px' }} />
                </button>
              )}
              <button
                type="button"
                className="icon-btn"
                onClick={onAddInvoiceItem}
                title="Add Item to Invoice"
              >
                <Receipt style={{ fontSize: '16px' }} />
              </button>
            </div>
          </div>
          <div className="workitem-details-content-box">
            <div className="detail-row">
              <span className="detail-label">Type:</span>
              <span className="detail-value">{workItem.type.toUpperCase()}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Title:</span>
              <span className="detail-value">{workItem.title}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Customer:</span>
              <span className="detail-value">{workItem.customerName}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Status:</span>
              <span className="detail-value">{workItem.status}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Created:</span>
              <span className="detail-value">{formatTimestamp(workItem.createdOn)}</span>
            </div>
            {workItem.startAt && (
              <div className="detail-row">
                <span className="detail-label">Start Time:</span>
                <span className="detail-value">{formatTimestamp(workItem.startAt)}</span>
              </div>
            )}
            {workItem.closedDate && (
              <div className="detail-row">
                <span className="detail-label">Closed:</span>
                <span className="detail-value">{formatTimestamp(workItem.closedDate)}</span>
              </div>
            )}
            
            {/* Job-specific details */}
            {job && (
              <>
                <div className="detail-row">
                  <span className="detail-label">Job Number:</span>
                  <span className="detail-value">{job.number}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Problem Type:</span>
                  <span className="detail-value">{job.problemType || 'Not specified'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Tags:</span>
                  <span className="detail-value">{job.tags ? job.tags.join(', ') : 'No tags'}</span>
                </div>
                {job.dueDate && (
                  <div className="detail-row">
                    <span className="detail-label">Due Date:</span>
                    <span className="detail-value">{formatTimestamp(job.dueDate)}</span>
                  </div>
                )}
              </>
            )}
            
            {/* Appointment-specific details */}
            {appointment && (
              <>
                <div className="detail-row">
                  <span className="detail-label">Appointment Number:</span>
                  <span className="detail-value">{appointment.number}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Type:</span>
                  <span className="detail-value">{appointment.type}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Problem Type:</span>
                  <span className="detail-value">{appointment.problemType || 'Not specified'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">End Time:</span>
                  <span className="detail-value">{appointment.endAt ? formatTimestamp(appointment.endAt) : 'No end time set'}</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Work Notes Section - only for jobs */}
        {job && (
          <WorkNotesSection
            job={job}
            workNotesHandlers={workNotesHandlers}
          />
        )}

        {/* Appointments Section - show related appointments for jobs */}
        {job && (
          <AppointmentsSection
            job={job}
            appointments={appointments.filter(a => a.customerId === job.customerId)}
            appointmentsHandlers={appointmentsHandlers}
          />
        )}
      </div>

      {/* Appointment Form */}
      {showAppointmentForm && job && (
        <AppointmentForm
          job={job}
          jobs={jobs}
          appointmentsHandlers={appointmentsHandlers}
          isVisible={showAppointmentForm}
          onClose={handleCloseAppointmentForm}
        />
      )}
    </div>
  );
};